import { client } from "./client";
import { decodeAsset } from "./decode";
import type { PaginatedAssets, SnipeItAsset } from "./types";

/**
 * Maps internal status-type strings (matching Snipe-IT status_label.status_type)
 * to the values accepted by the /api/v1/hardware `status` query parameter.
 */
const STATUS_TYPE_MAP: Record<string, string> = {
  deployable: "RTD",
  deployed: "Deployed",
  undeployable: "Undeployable",
  pending: "Pending",
  archived: "Archived",
};

export function toApiStatus(statusType: string): string {
  return STATUS_TYPE_MAP[statusType] ?? statusType;
}

export async function searchHardwareByField(
  query: string,
  field: "asset_tag" | "serial" | "name" | ("asset_tag" | "serial" | "name")[],
  opts?: { locationId?: number; statusType?: string },
): Promise<SnipeItAsset[]> {
  if (!query) return [];
  const params: Record<string, unknown> = { search: query, limit: 50 };
  if (opts?.locationId !== undefined) params.location_id = opts.locationId;
  if (opts?.statusType) params.status = toApiStatus(opts.statusType);
  const res = await client.get<{ rows: SnipeItAsset[] }>("/api/v1/hardware", { params });
  const needle = query.toLowerCase();
  const fields = Array.isArray(field) ? field : [field];
  return (res.data.rows ?? [])
    .filter((a) => fields.some((f) => (a[f] ?? "").toLowerCase().includes(needle)))
    .map(decodeAsset);
}

export async function checkinHardware(assetId: number, locationId?: number, statusId?: number): Promise<void> {
  const payload: Record<string, number> = {};
  if (locationId !== undefined) payload.location_id = locationId;
  if (statusId !== undefined) payload.status_id = statusId;
  await client.post(`/api/v1/hardware/${assetId}/checkin`, payload);
}

export async function checkoutHardware(assetId: number, userId: number): Promise<void> {
  await client.post(`/api/v1/hardware/${assetId}/checkout`, {
    checkout_to_type: "user",
    assigned_user: userId,
  });
}

export async function fetchLocationAssetsPage(options: {
  locationId: number;
  limit: number;
  offset: number;
  sort: string;
  order: "asc" | "desc";
  search?: string;
  modelId?: number;
  categoryId?: number;
  statusType?: string;
}): Promise<PaginatedAssets> {
  const params: Record<string, unknown> = {
    location_id: options.locationId,
    limit: options.limit,
    offset: options.offset,
    sort: options.sort,
    order: options.order,
  };
  if (options.search) params.search = options.search;
  if (options.modelId !== undefined) params.model_id = options.modelId;
  if (options.categoryId !== undefined) params.category_id = options.categoryId;
  if (options.statusType) params.status = toApiStatus(options.statusType);

  const res = await client.get<{ total: number; rows: SnipeItAsset[] }>("/api/v1/hardware", { params });
  return {
    total: res.data.total,
    rows: (res.data.rows ?? []).map(decodeAsset),
  };
}

export async function updateAssetLocation(assetId: number, locationId: number): Promise<void> {
  await client.patch(`/api/v1/hardware/${assetId}`, { rtd_location_id: locationId, location_id: locationId });
}

export async function updateAssetTag(assetId: number, assetTag: string): Promise<void> {
  await client.patch(`/api/v1/hardware/${assetId}`, { asset_tag: assetTag });
}

/** Returns the total number of hardware assets for a given model ID, optionally scoped to a location. */
export async function countHardwareByModel(modelId: number, locationId?: number): Promise<number> {
  const params: Record<string, unknown> = { model_id: modelId, limit: 1, offset: 0 };
  if (locationId !== undefined) params.location_id = locationId;
  const res = await client.get<{ total: number }>("/api/v1/hardware", { params });
  return res.data.total;
}

export interface CreateHardwarePayload {
  model_id: number;
  status_id: number;
  rtd_location_id?: number;
  location_id?: number;
  serial?: string;
  asset_tag?: string;
  company_id?: number;
}

export async function createHardware(payload: CreateHardwarePayload): Promise<void> {
  const res = await client.post("/api/v1/hardware", payload);
  if (res.data?.status === "error") {
    throw new Error(res.data.messages || "Beim Erstellen ist ein Fehler aufgetreten");
  }
}
